import Styles from './ChargingPeriodAccordion.module.css'
import Colors from 'Theme/Colors'
import { IFText, IFTextInput } from 'Components'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { DatePicker } from 'rsuite'
import validator from 'validator'
import PropTypes from 'prop-types'
import { DoNotDisturbOn } from '@mui/icons-material'
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded'

const ChargingPeriodAccordionItem = ({
  id,
  index,
  value,
  showRemove,
  removed,
  onChange,
  onErrorChange,
  onToggleRemove,
}) => {
  const { t } = useTranslation()
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const [startTime, setStartTime] = useState(value?.startTime ?? null)
  const [time, setTime] = useState(value?.time ?? '')
  const [power, setPower] = useState(value?.power ?? '')
  const [stateOfCharge, setStateOfCharge] = useState(value?.stateOfCharge ?? '')
  const [energy, setEnergy] = useState(value?.energy ?? '')
  const [current, setCurrent] = useState(value?.current ?? '')
  const [parkingTime, setParkingTime] = useState(value?.parkingTime ?? '')

  const [energyError, setEnergyError] = useState('')
  const [currentError, setCurrentError] = useState('')
  const [parkingTimeError, setParkingTimeError] = useState('')
  const [timeError, setTimeError] = useState('')
  const [stateOfChargeError, setStateOfChargeError] = useState('')
  const [powerError, setPowerError] = useState('')

  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    onErrorChange?.(hasError)
  }, [hasError, onErrorChange])

  useEffect(() => {
    onChangeRef.current?.({
      id,
      startTime,
      stateOfCharge,
      energy,
      current,
      parkingTime,
      time,
      power,
    })
  }, [id, startTime, stateOfCharge, energy, current, parkingTime, time, power])

  return (
    <div className={Styles.ContainerDiv}>
      <Accordion
        sx={{
          bgcolor: Colors.Accordion,
          color: Colors.text,
          '& .MuiAccordionSummary-root': {
            bgcolor: Colors.Accordion,
            color: Colors.text,
          },
          '& .MuiAccordionDetails-root': {
            bgcolor: Colors.Accordion,
            color: Colors.text,
          },
          boxShadow: 0,
          border: '1.5px solid ',
          borderColor: Colors.DividerColor,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`restrictions-content-${id}`}
          id={`restrictions-header-${id}`}
        >
          <div className={Styles.TitleContainer}>
            {showRemove &&
              (removed ? (
                <AddCircleRoundedIcon
                  style={{ color: Colors.ChargePointTariffListItem.AddIcon }}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    onToggleRemove()
                  }}
                  className={Styles.RemoveIcon}
                />
              ) : (
                <DoNotDisturbOn
                  className={Styles.RemoveIcon}
                  style={{ color: Colors.ChargePointTariffListItem.RemoveIcon }}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    onToggleRemove()
                  }}
                />
              ))}

            <IFText
              className={Styles.TitleText}
              style={{
                color: removed
                  ? Colors.ChargePointTariffListItem.gray
                  : Colors.text,
              }}
            >
              {t('ChargingPeriodAccordion.ChargingPeriod')} {index + 1}
            </IFText>
          </div>
        </AccordionSummary>

        <AccordionDetails>
          <div className={Styles.Container}>
            <div className={Styles.InputFullWidth}>
              <div className={Styles.InputItem}>
                <div className={Styles.InputLabel}>
                  <IFText
                    style={{ color: Colors.DetailPanel }}
                    className={Styles.LabelText}
                  >
                    {t('ChargingPeriodAccordion.StartTime')}
                  </IFText>
                </div>
                <div className={Styles.InputField}>
                  <DatePicker
                    className={Styles.DatePicker}
                    value={startTime || null}
                    onChange={(val) => {
                      setStartTime(val)
                    }}
                    cleanable
                    size="sm"
                    placeholder="yyyy-MM-dd hh:mm aa"
                    format="yyyy-MM-dd hh:mm aa"
                    showMeridian
                    style={{
                      minWidth: '12rem',
                      maxWidth: '12rem',
                      minHeight: '1.5rem',
                    }}
                    placement="autoVerticalStart"
                  />
                </div>
              </div>
            </div>
            <div className={Styles.InputTwoColumns}>
              <div className={Styles.InputLeftColumn}>
                <div className={Styles.InputItem}>
                  <div className={Styles.InputLabel}>
                    <IFText
                      style={{ color: Colors.DetailPanel }}
                      className={Styles.LabelText}
                    >
                      {t('ChargingPeriodAccordion.Time')}
                    </IFText>
                  </div>
                  <div className={Styles.InputField}>
                    <IFTextInput
                      className={Styles.TextInput}
                      value={time}
                      onChange={(e) => {
                        const raw = e.target.value
                        const value = raw.trim()
                        setTime(raw)

                        if (value === '') {
                          setTimeError(undefined)
                          setHasError(false)
                          return
                        }

                        if (!validator.isFloat(value)) {
                          setTimeError(t('TariffModal.Invalid'))
                          setHasError(true)
                          return
                        }

                        setTimeError(undefined)
                        setHasError(false)
                      }}
                      errorText={timeError}
                    />
                  </div>
                </div>
                <div className={Styles.InputItem}>
                  <div className={Styles.InputLabel}>
                    <IFText
                      style={{ color: Colors.DetailPanel }}
                      className={Styles.LabelText}
                    >
                      {t('ChargingPeriodAccordion.Power')}
                    </IFText>
                  </div>
                  <div className={Styles.InputField}>
                    <IFTextInput
                      className={Styles.TextInput}
                      value={power}
                      onChange={(e) => {
                        const raw = e.target.value
                        const value = raw.trim()
                        setPower(raw)

                        if (value === '') {
                          setPowerError(undefined)
                          setHasError(false)
                          return
                        }

                        if (!validator.isFloat(value)) {
                          setPowerError(t('TariffModal.Invalid'))
                          setHasError(true)
                          return
                        }

                        setPowerError(undefined)
                        setHasError(false)
                      }}
                      errorText={powerError}
                    />
                  </div>
                </div>
                <div className={Styles.InputItem}>
                  <div className={Styles.InputLabel}>
                    <IFText
                      style={{ color: Colors.DetailPanel }}
                      className={Styles.LabelText}
                    >
                      {t('ChargingPeriodAccordion.StateOfCharge')}
                    </IFText>
                  </div>
                  <div className={Styles.InputField}>
                    <IFTextInput
                      className={Styles.TextInput}
                      value={stateOfCharge}
                      onChange={(e) => {
                        const raw = e.target.value
                        const value = raw.trim()
                        setStateOfCharge(raw)

                        if (value === '') {
                          setStateOfChargeError(undefined)
                          setHasError(false)
                          return
                        }

                        if (!validator.isFloat(value)) {
                          setStateOfChargeError(t('TariffModal.Invalid'))
                          setHasError(true)
                          return
                        }

                        setStateOfChargeError(undefined)
                        setHasError(false)
                      }}
                      errorText={stateOfChargeError}
                    />
                  </div>
                </div>
              </div>

              <div className={Styles.InputRightColumn}>
                <div className={Styles.InputItem}>
                  <div className={Styles.InputLabel}>
                    <IFText
                      style={{ color: Colors.DetailPanel }}
                      className={Styles.LabelText}
                    >
                      {t('ChargingPeriodAccordion.Energy')}
                    </IFText>
                  </div>
                  <div className={Styles.InputField}>
                    <IFTextInput
                      className={Styles.TextInput}
                      value={energy}
                      onChange={(e) => {
                        const raw = e.target.value
                        const value = raw.trim()
                        setEnergy(raw)

                        if (value === '') {
                          setEnergyError(undefined)
                          setHasError(false)
                          return
                        }

                        if (!validator.isFloat(value)) {
                          setEnergyError(t('TariffModal.Invalid'))
                          setHasError(true)
                          return
                        }
                        setEnergyError(undefined)
                        setHasError(false)
                      }}
                      errorText={energyError}
                    />
                  </div>
                </div>

                <div className={Styles.InputItem}>
                  <div className={Styles.InputLabel}>
                    <IFText
                      style={{ color: Colors.DetailPanel }}
                      className={Styles.LabelText}
                    >
                      {t('ChargingPeriodAccordion.Current')}
                    </IFText>
                  </div>
                  <div className={Styles.InputField}>
                    <IFTextInput
                      className={Styles.TextInput}
                      value={current}
                      onChange={(e) => {
                        const raw = e.target.value
                        const value = raw.trim()
                        setCurrent(raw)

                        if (value === '') {
                          setCurrentError(undefined)
                          setHasError(false)
                          return
                        }

                        if (!validator.isFloat(value)) {
                          setCurrentError(t('TariffModal.Invalid'))
                          setHasError(true)
                          return
                        }

                        setCurrentError(undefined)
                        setHasError(false)
                      }}
                      errorText={currentError}
                    />
                  </div>
                </div>

                <div className={Styles.InputItem}>
                  <div className={Styles.InputLabel}>
                    <IFText
                      style={{ color: Colors.DetailPanel }}
                      className={Styles.LabelText}
                    >
                      {t('ChargingPeriodAccordion.ParkingTime')}
                    </IFText>
                  </div>
                  <div className={Styles.InputField}>
                    <IFTextInput
                      className={Styles.TextInput}
                      value={parkingTime}
                      onChange={(e) => {
                        const raw = e.target.value
                        const value = raw.trim()
                        setParkingTime(raw)

                        if (value === '') {
                          setParkingTimeError(undefined)
                          setHasError(false)
                          return
                        }

                        if (!validator.isFloat(value)) {
                          setParkingTimeError(t('TariffModal.Invalid'))
                          setHasError(true)
                          return
                        }

                        setParkingTimeError(undefined)
                        setHasError(false)
                      }}
                      errorText={parkingTimeError}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  )
}

ChargingPeriodAccordionItem.propTypes = {
  onChange: PropTypes.func,
  onErrorChange: PropTypes.func,
  onRemove: PropTypes.func,
  id: PropTypes.string,
  value: PropTypes.object,
  showRemove: PropTypes.bool,
  index: PropTypes.number,
}

export default ChargingPeriodAccordionItem
